import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  normalizeSponsor,
  sortSponsors,
  staticSponsorsAsDocs,
} from '../utils/sponsorsCatalog';

function visibleStatic() {
  return staticSponsorsAsDocs().filter((s) => s.active && s.logoUrl);
}

function mergeSponsors(fromFs, { includeInactive }) {
  const staticDocs = staticSponsorsAsDocs();
  const presentIds = new Set(fromFs.map((s) => s.id));
  const fsList = includeInactive ? fromFs : fromFs.filter((s) => s.active && s.logoUrl);
  const fromStatic = staticDocs.filter((s) => !presentIds.has(s.id) && (includeInactive || s.logoUrl));
  return sortSponsors([...fsList, ...fromStatic]);
}

/**
 * Live sponsors = Firestore `sponsors` overlaid on the built-in 2026–2027 list.
 * Soft-deleted docs (active === false) stay hidden and block the built-in logo.
 * Home page uses active logos only. Admin passes includeInactive so hidden sponsors can be shown again.
 */
export default function useSponsors({ includeInactive = false } = {}) {
  const [sponsors, setSponsors] = useState(() =>
    includeInactive ? staticSponsorsAsDocs() : visibleStatic()
  );
  const [source, setSource] = useState('static');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'sponsors'),
      (snap) => {
        const staticDocs = staticSponsorsAsDocs();
        if (snap.empty) {
          setSponsors(includeInactive ? staticDocs : visibleStatic());
          setSource('static');
          setError(null);
          setLoading(false);
          return;
        }

        const fromFs = snap.docs.map((d) => normalizeSponsor({ id: d.id, ...d.data() }));
        const merged = mergeSponsors(fromFs, { includeInactive });
        const presentIds = new Set(fromFs.map((s) => s.id));
        const staticLeft = staticDocs.filter((s) => !presentIds.has(s.id));
        const activeFs = fromFs.filter((s) => s.active !== false);

        setSponsors(merged);
        if (staticLeft.length === 0) setSource('firestore');
        else if (activeFs.length === 0) setSource('static');
        else setSource('hybrid');
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('sponsors listen:', err);
        setSponsors(includeInactive ? staticSponsorsAsDocs() : visibleStatic());
        setSource('static');
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [includeInactive]);

  return { sponsors, source, loading, error };
}
